import { useState, useEffect } from 'react';
import { Settings, Server, Cpu, Clock, Shield, Check, ChevronDown, Copy, Lock } from 'lucide-react';
import { usePoolStore } from '@/stores/usePoolStore';
import { config } from '@/config';
import clsx from 'clsx';

/** localStorage key for portal settings. */
const SETTINGS_STORAGE_KEY = 'w365a-settings';

interface PortalSettings {
  endpoint: string;
  processorCount: string;
  memorySize: string;
  maxIdleInSeconds: number;
  maxLifetimeInSeconds: number;
}

const DEFAULT_SETTINGS: PortalSettings = {
  endpoint: config.sandboxMgmtUrl,
  processorCount: '4',
  memorySize: '8Gi',
  maxIdleInSeconds: 3600,
  maxLifetimeInSeconds: 28800,
};

const IDLE_TIMEOUT_OPTIONS = [
  { label: '30 minutes', value: 1800 },
  { label: '1 hour', value: 3600 },
  { label: '2 hours', value: 7200 },
];

const LIFETIME_OPTIONS = [
  { label: '4 hours', value: 14400 },
  { label: '8 hours', value: 28800 },
  { label: '24 hours', value: 86400 },
];

function loadSettings(): PortalSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (raw) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
    }
  } catch {
    // Ignore parse errors, fall through to defaults
  }
  return { ...DEFAULT_SETTINGS };
}

/** Read-only field styled to match input height exactly. */
function ReadOnlyField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div>
      <label className="block text-xs font-medium text-surface-500 dark:text-surface-400 mb-1.5">
        {label}
      </label>
      <div className="relative group">
        <input
          type="text"
          readOnly
          tabIndex={-1}
          value={value}
          className={clsx(
            'input bg-surface-50 dark:bg-white/[0.06]',
            'text-surface-600 dark:text-surface-400',
            'cursor-default select-all',
            mono && 'font-mono text-xs'
          )}
          onFocus={(e) => e.currentTarget.blur()}
        />
        {value && (
          <button
            onClick={handleCopy}
            className={clsx(
              'absolute right-1.5 top-1/2 -translate-y-1/2',
              'p-1 rounded transition-all duration-150',
              'opacity-0 group-hover:opacity-100',
              'text-surface-400 hover:text-surface-600 dark:hover:text-surface-300',
              'hover:bg-surface-200/60 dark:hover:bg-surface-700/60',
              copied && 'opacity-100 text-green-600 dark:text-green-400'
            )}
            title="Copy"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}

/** Styled select dropdown with custom chevron. */
function SelectField({
  label,
  icon,
  value,
  options,
  hint,
  onChange,
}: {
  label: string;
  icon?: React.ReactNode;
  value: number;
  options: { label: string; value: number }[];
  hint?: string;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-surface-500 dark:text-surface-400 mb-1.5">
        <span className="flex items-center gap-1.5">
          {icon}
          {label}
        </span>
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="input pr-9 appearance-none cursor-pointer"
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400 pointer-events-none" />
      </div>
      {hint && (
        <p className="mt-1.5 text-xs text-surface-400">{hint}</p>
      )}
    </div>
  );
}

/** Text input field with label and hint. */
function InputField({
  label,
  value,
  placeholder,
  hint,
  onChange,
}: {
  label: string;
  value: string;
  placeholder?: string;
  hint?: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-surface-500 dark:text-surface-400 mb-1.5">
        {label}
      </label>
      <input
        type="text"
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {hint && (
        <p className="mt-1.5 text-xs text-surface-400">{hint}</p>
      )}
    </div>
  );
}

export function SettingsPage() {
  const { getSelectedPool } = usePoolStore();
  const selectedPool = getSelectedPool();

  const [settings, setSettings] = useState<PortalSettings>(loadSettings);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), 2000);
    return () => clearTimeout(timer);
  }, [saved]);

  function updateField<K extends keyof PortalSettings>(key: K, value: PortalSettings[K]) {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave() {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    setSaved(true);
  }

  return (
    <div className="p-6 lg:p-8 max-w-3xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-surface-900 dark:text-surface-100">Settings</h1>
        <p className="text-sm text-surface-500 dark:text-surface-400 mt-0.5">
          Configure portal defaults
        </p>
      </div>

      <div className="space-y-4">
        {/* Connection Card */}
        <div className="card animate-stagger-1">
          <div className="px-4 py-3 border-b border-surface-200 dark:border-white/[0.06] flex items-center gap-2">
            <Server className="w-4 h-4 text-brand-500" />
            <h2 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
              Connection
            </h2>
          </div>

          <div className="p-5 space-y-4">
            <InputField
              label="API Endpoint"
              value={settings.endpoint}
              placeholder="https://api.wcp.microsoft.com"
              hint="The base URL for WCP API endpoints"
              onChange={(v) => updateField('endpoint', v)}
            />

            <ReadOnlyField
              label="Current Pool"
              value={
                selectedPool
                  ? `${selectedPool.displayName} (${selectedPool.poolId})`
                  : 'No pool selected'
              }
            />
          </div>
        </div>

        {/* Default Sandbox Configuration Card */}
        <div className="card animate-stagger-2">
          <div className="px-4 py-3 border-b border-surface-200 dark:border-white/[0.06] flex items-center gap-2">
            <Cpu className="w-4 h-4 text-brand-500" />
            <h2 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
              Default Sandbox Configuration
            </h2>
          </div>

          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InputField
                label="Default CPU"
                value={settings.processorCount}
                placeholder="4"
                hint="Kubernetes format (e.g. 4, 500m)"
                onChange={(v) => updateField('processorCount', v)}
              />
              <InputField
                label="Default Memory"
                value={settings.memorySize}
                placeholder="8Gi"
                hint="Kubernetes format (e.g. 8Gi, 4096Mi)"
                onChange={(v) => updateField('memorySize', v)}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SelectField
                label="Max Idle Timeout"
                icon={<Clock className="w-3.5 h-3.5" />}
                value={settings.maxIdleInSeconds}
                options={IDLE_TIMEOUT_OPTIONS}
                hint={`${settings.maxIdleInSeconds.toLocaleString()}s`}
                onChange={(v) => updateField('maxIdleInSeconds', v)}
              />
              <SelectField
                label="Max Lifetime"
                icon={<Clock className="w-3.5 h-3.5" />}
                value={settings.maxLifetimeInSeconds}
                options={LIFETIME_OPTIONS}
                hint={`${settings.maxLifetimeInSeconds.toLocaleString()}s`}
                onChange={(v) => updateField('maxLifetimeInSeconds', v)}
              />
            </div>
          </div>
        </div>

        {/* Authentication Card */}
        <div className="card animate-stagger-3">
          <div className="px-4 py-3 border-b border-surface-200 dark:border-white/[0.06] flex items-center gap-2">
            <Shield className="w-4 h-4 text-brand-500" />
            <h2 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
              Authentication
            </h2>
            <Lock className="w-3 h-3 text-surface-400 ml-auto" />
          </div>

          <div className="p-5 space-y-4">
            <ReadOnlyField
              label="Tenant ID"
              value={config.azureTenantId || 'Not configured'}
              mono
            />
            <ReadOnlyField
              label="API Scope"
              value={config.azureApiScope || 'Not configured'}
              mono
            />

            <div className="flex items-center gap-2 pt-1">
              <div className="w-1 h-1 rounded-full bg-surface-300 dark:bg-surface-600" />
              <p className="text-xs text-surface-400">
                Configured via environment variables
              </p>
            </div>
          </div>
        </div>

        {/* About Card */}
        <div className="card animate-stagger-4">
          <div className="px-4 py-3 border-b border-surface-200 dark:border-white/[0.06]">
            <h2 className="text-sm font-semibold text-surface-900 dark:text-surface-100">About</h2>
          </div>

          <div className="p-5">
            <div className="flex items-start gap-3">
              <div className="bg-brand-500 p-2 rounded-fluent-md flex-shrink-0">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-surface-900 dark:text-surface-100 mb-0.5">
                  {config.appName}
                </h3>
                <p className="text-xs text-surface-500 font-mono mb-2">
                  v{config.appVersion}
                </p>
                <div className="text-xs text-surface-400 space-y-0.5">
                  <p>Built with React, TypeScript, and Tailwind CSS</p>
                  <p>&copy; 2025 Microsoft Corporation</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button — sticky footer feel */}
      <div className="mt-6 flex items-center gap-3">
        <button
          className="btn-primary"
          onClick={handleSave}
        >
          {saved ? (
            <>
              <Check className="w-4 h-4 mr-1.5" />
              Saved
            </>
          ) : (
            'Save Settings'
          )}
        </button>
        {saved && (
          <span className="text-xs text-green-600 dark:text-green-400 animate-pulse">
            Changes persisted to local storage
          </span>
        )}
      </div>
    </div>
  );
}
